import { DatePicker } from '@mui/lab';
import { Grid } from '@mui/material';
import { MunicipalityFormVM } from 'civil-status/view-models/municipality-form';
import { useFormikContext } from 'formik';
import { ReactElement, useEffect } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, {
  MunicipalityAnpr,
  MunicipalityAnprVM,
} from 'shell/components/shared/municipality-list';
import { useEventCallback } from '@mui/material';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { DateTime } from 'luxon';

interface FormMunicipalityProps {
  disabled?: boolean;
  isEdit?: boolean;
  isDetail?: boolean;
}

const FormMunicipality = ({
  disabled = false,
  isEdit = false,
  isDetail = false,
}: FormMunicipalityProps): ReactElement => {
  const formContext = useFormikContext<MunicipalityFormVM>();

  const onChangeMunicipality = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    formContext.setValues({
      ...(formContext?.values || {}),
      anscMunicipality: municipality,
      idAnsc: municipality?.code?.toString() || '',
    });
  });

  const [municipality, getMunicipality] = useHttpFetch<
    HttpResponse<MunicipalityAnpr>,
    MunicipalityAnprVM
  >(`${__MUNICIPALITY_API_URL__}/geo/comune/anpr`, {
    onBootstrap: false,
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponse<MunicipalityAnpr>>) => {
      const { data } = response;

      const res: MunicipalityAnprVM = {
        id: data?.payload?.codiceIstat,
        description: data?.payload?.denominazione,
        provinceCode: data?.payload?.idProvinciaIstat?.toString() || '',
        province: data?.payload?.codiceSiglaProvincia,
        startDate: data?.payload?.dataIstituzione,
        endDate: data?.payload?.dataCessazione,
        code: data?.payload?.key?.codice || undefined,
        idProvince: data?.payload?.idProvincia
          ? Number.parseInt(data?.payload?.idProvincia, 10)
          : undefined,
      };

      return res;
    },
  });

  useEffect(() => {
    if (!isDetail && !isEdit) return;

    if (formContext.values?.idAnsc) {
      getMunicipality({
        url: `/${formContext.values?.idAnsc}`,
      });
    }
  }, [isDetail, isEdit, formContext.values?.idAnsc]);

  useEffect(() => {
    if (!isDetail && !isEdit) return;

    if (municipality?.data) {
      formContext.setFieldValue('anscMunicipality', { ...(municipality?.data || {}) });
    }
  }, [isDetail, municipality?.data, isEdit]);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Ente *"
          aria-label="Ente"
          name="institution"
          id="institution"
          fullWidth
          value={formContext.values?.institution || ''}
          onChange={(event) => formContext.setFieldValue('institution', event.target.value)}
          disabled
          error={formContext.touched?.institution && Boolean(formContext.errors?.institution)}
          helperText={formContext.errors?.institution}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID *"
          aria-label="ID"
          name="id"
          id="id"
          fullWidth
          value={formContext.values?.id || ''}
          onChange={(event) => formContext.setFieldValue('id', event.target.value)}
          disabled={disabled || isEdit}
          error={formContext.touched?.id && Boolean(formContext.errors?.id)}
          helperText={formContext.errors?.id}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <MunicipalityList
          value={formContext?.values?.anscMunicipality || null}
          onChange={onChangeMunicipality}
          required
          label="Comune ANPR"
          id="anscMunicipality"
          disabled={disabled}
          fromDate={DateTime.now()}
          error={
            formContext.touched?.anscMunicipality && Boolean(formContext.errors?.anscMunicipality)
          }
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID Comune ANPR *"
          aria-label="ID Comune ANPR"
          name="idAnsc"
          id="idAnsc"
          fullWidth
          value={formContext.values?.idAnsc || ''}
          onChange={(event) => formContext.setFieldValue('idAnsc', event.target.value)}
          disabled
          error={formContext.touched?.idAnsc && Boolean(formContext.errors?.idAnsc)}
          helperText={formContext.errors?.idAnsc}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data inizio validità"
          aria-label="Data inizio validità"
          disabled={disabled}
          value={formContext.values?.dateFrom || null}
          onChange={(date) => formContext.setFieldValue('dateFrom', date)}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateFrom"
              fullWidth
              error={formContext.touched?.dateFrom && Boolean(formContext.errors?.dateFrom)}
              helperText={formContext.errors?.dateFrom}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data fine validità"
          aria-label="Data fine validità"
          disabled={disabled}
          value={formContext.values?.dateTo || null}
          onChange={(date) => formContext.setFieldValue('dateTo', date)}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateTo"
              fullWidth
              error={formContext.touched?.dateTo && Boolean(formContext.errors?.dateTo)}
              helperText={formContext.errors?.dateTo}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="N. ordinamento"
          aria-label="N. ordinamento"
          name="order"
          id="order"
          fullWidth
          value={formContext.values?.order || ''}
          onChange={(event) => formContext.setFieldValue('order', event.target.value)}
          disabled={disabled}
          error={formContext.touched?.order && Boolean(formContext.errors?.order)}
          helperText={formContext.errors?.order}
          type="number"
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Descrizione *"
          aria-label="Descrizione"
          name="description"
          id="description"
          fullWidth
          value={formContext.values?.description || ''}
          onChange={(event) => formContext.setFieldValue('description', event.target.value)}
          disabled={disabled}
          error={formContext.touched?.description && Boolean(formContext.errors?.description)}
          helperText={formContext.errors?.description}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note contesto ultimo aggiornamento"
          aria-label="Note contesto ultimo aggiornamento"
          name="note"
          id="note"
          fullWidth
          value={formContext.values?.note || ''}
          onChange={(event) => formContext.setFieldValue('note', event.target.value)}
          disabled={disabled}
          error={formContext.touched?.note && Boolean(formContext.errors?.note)}
          helperText={formContext.errors?.note}
        />
      </Grid>
    </Grid>
  );
};

FormMunicipality.displayName = 'Municipality - Form Page component';

export default FormMunicipality;
